import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faClone, faMegaphone, faPlus } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";

interface CampaignsEmptyStateProps {
	/** Start the create-campaign flow (opens the event/regular branch modal when applicable). */
	onCreate: () => void;
	/** Open the "copy campaigns from another list" modal. */
	onCopy: () => void;
	/** Skip campaigns and continue to the list settings step. */
	onSkip: () => void;
}

/**
 * Empty state for the Campaigns step when a list has no campaigns yet (PREC-3520 mockup
 * "signalist-event-campaigns-v2.html" — Empty flow). Mirrors the centered-card pattern used by
 * other list empty states (e.g. CatalogFavoritesEmpty).
 */
export const CampaignsEmptyState: FunctionComponent<CampaignsEmptyStateProps> = ({
	onCreate,
	onCopy,
	onSkip,
}) => {
	const { t } = useTranslation();
	const tc = (key: string) => t(`list.outreach.empty.${key}`);

	return (
		<div className="mx-auto mt-8 max-w-2xl rounded-xl border border-border-light bg-white p-12 text-center">
			<div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-light-blue">
				<Icon className="h-6 w-6 text-brand-light" icon={faMegaphone} />
			</div>
			<h2 className="mb-1.5 text-lg font-bold text-text">{tc("title")}</h2>
			<p className="mx-auto mb-6 max-w-md text-sm leading-6 text-text-meta">{tc("description")}</p>
			<div className="flex flex-wrap items-center justify-center gap-3">
				<Button
					color={ButtonColor.ACTION}
					image={<Icon className="mr-2" icon={faPlus} />}
					title={tc("createNew")}
					onClick={onCreate}
				/>
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					image={<Icon className="mr-2" icon={faClone} />}
					title={tc("copyFromList")}
					onClick={onCopy}
				/>
			</div>
			<div className="mt-5 text-sm text-text-meta">
				{tc("skipPrompt")}{" "}
				<button
					className="font-semibold text-brand-light underline underline-offset-2 hover:no-underline"
					type="button"
					onClick={onSkip}
				>
					{tc("skipLink")}
				</button>
			</div>
		</div>
	);
};
